import api from '../../api';
const cloneDeep = require('lodash/cloneDeep');
import { isEmpty } from '../../utils/objects';
import math from '../../utils/math';
import timeout from '../../utils/timeout';
import { dom, numbers } from '../../utils';

const CALCULATE_SELF = '<SELF>';

export default {
    namespaced: true,
    state: {
        fields: {},
        formData: {},
        mathScope: {},
        dependents: {},
        scrollPageIndex: -1,
        scrolledToItem: -1,
        hasUnsavedChanges: false,
        isOffice: false,
        submittedByFname: '',
        submittedByLname: '',
        submittedByEmail: '',
        revisionRef: '',
        signingFeature: null,
        signatureLocks: {},
        isLoaded: false,
        recipient: null,
        highlightedSignatureId: null,
        pin: '',
        invalidIds: [],
    },
    getters: {
        formData(state) {
            return state.formData;
        },
        fieldData: state => (formId, fieldId) => {
            if (!state.formData[formId]) return {};
            return state.formData[formId][fieldId] || {};
        },
        fieldDefaultValue: (state, getters, rootState, rootGetters) => fieldId => {
            let feature = rootGetters['form/featureById'](fieldId);
            if (feature.defaultData) return null; // Don't overwrite default data values
            if (feature.type === 'textbox' || feature.type === 'label' || feature.type === 'link') {
                return feature.text || '';
            }
            if (feature.type === 'checkbox') {
                return feature.checked ? (feature.value || '1') : '';
            }
            if (feature.type === 'dropdown') {
                let def = feature.options.find(opt => opt.isDefault);
                return def ? def.value : '';
            }
            return '';
        },
        fieldQueryStringValue: (state, getters, rootState, rootGetters) => (fieldId) => {
            let feature = rootGetters['form/featureById'](fieldId);
            if (feature.defaultData) return null; // Don't overwrite default data values

            // Pull default value from query string if the feature/group name matches
            let query = rootGetters['base/currentRoute'].query;
            let queryVal;
            let groupName = feature.groupId ? rootGetters['form/featureGroups'][feature.groupId].name : null;
            if (groupName && query.hasOwnProperty(groupName)) {
                let featurVal = feature.value || '1';
                queryVal = query[groupName] == featurVal ? featurVal : '';
            } else if (query.hasOwnProperty(feature.name)) {
                queryVal = query[feature.name].toString();
            } else {
                return null;
            }
            if (feature.type === 'dropdown') {
                let option = feature.options.find(opt => opt.value === queryVal);
                return option ? option.value : '';
            } else if (feature.type === 'textbox' || feature.type === 'label' || feature.type === 'checkbox') {
                return queryVal;
            }
            return null;
        },
        scrollPageIndex(state) {
            return state.scrollPageIndex;
        },
        scrolledToItem(state) {
            return state.scrolledToItem;
        },
        hasUnsavedChanges(state) {
            return state.hasUnsavedChanges;
        },
        formPageCount: (state, getters, rootState, rootGetters) => id => {
            let form = rootGetters['form/formById'](id);
            return form ? form.data.pages.length : 0;
        },
        isOffice(state) {
            return state.isOffice;
        },
        submittedByFname(state) {
            return state.submittedByFname;
        },
        submittedByLname(state) {
            return state.submittedByLname;
        },
        submittedByEmail(state) {
            return state.submittedByEmail;
        },
        revisionRef(state) {
            return state.revisionRef;
        },
        signingFeature(state) {
            return state.signingFeature;
        },
        isFeatureLocked: (state) => (formId, featureId) => {
            return _isFeatureLocked(state, formId, featureId);
        },
        isLoaded(state) {
            return state.isLoaded;
        },
        recipient(state) {
            return state.recipient;
        },
        highlightedSignature(state, getters, rootState, rootGetters) {
            let sigId = state.highlightedSignatureId;
            if (!sigId) return null;

            let formId = rootGetters['form/forms']
                .find(form => form.data.pages
                    .find(page => page.features
                        .find(feature => feature.id === sigId))).id;

            let sigFeature = rootGetters['form/featureById'](sigId);

            return { ...sigFeature, isComplete: getters.fieldData(formId, sigId).signed };
        },
        baseEndpoint(state, getters, rootState, rootGetters) {
            return `filler/${rootGetters['package/tokenType']}/${rootGetters['package/altRegionCode']}/${rootGetters['package/accessToken']}`;
        },
        pin(state) {
            return state.pin;
        },
        invalidIds(state) {
            return state.invalidIds;
        },
        formPayments(state, getters, rootState, rootGetters) {
            let payments = [];
            for (let form of rootGetters['form/forms']) {
                if (!form.data.settings.paymentRequired) continue;
                let payment = { name: form.name, amount: 0 };
                if (form.data.settings.paymentCalculated) {
                    if (state.mathScope[form.id]) {
                        try {
                            let result = math.evaluate(form.data.settings.paymentAmountCalculate, state.mathScope[form.id]);
                            let float = parseFloat(result);
                            if (isNaN(float)) throw new Error(`"${result}"`);
                            payment.amount = numbers.roundToDecimal(float, 2);
                        } catch (err) {
                            payment.calculationError = err.message;
                            console.warn(`Invalid payment amount: ${err.message}`);
                        }
                    }
                } else {
                    payment.amount = parseFloat(form.data.settings.paymentAmount);
                }
                payments.push(payment);
            }
            return payments;
        },
        paymentTotal(state, getters) {
            return numbers.roundToDecimal(
                getters.formPayments.reduce((acc, payment) => acc + payment.amount, 0)
            , 2);
        },
        paymentCalculationError(state, getters) {
            return getters.formPayments.find((p) => p.calculationError)?.calculationError
        },
        isPaymentRequired(state, getters, rootState, rootGetters) {
            return (getters.paymentTotal || getters.paymentCalculationError) &&
                rootGetters['org/org'].settings.paymentProcessor &&
                !rootGetters['package/amountPaid']; // Only allow one payment per pkg for now
        },
        incompleteRequiredFieldsBefore: (state, getters, rootState, rootGetters) => (beforeWhat = 'before_submit') => {
            let reqTest;
            if (beforeWhat === 'before_submit') {
                reqTest = function(requiredVal, featureId = null, featureType = null) {
                    // if there are link-specific required signatures (set in the package Send Options), use those instead of the form default
                    if (featureType === 'signature') {
                        let pkgSpecificReqs = rootGetters['package/linkData'] && rootGetters['package/linkData'].required_signatures ? rootGetters['package/linkData'].required_signatures : null;
                        if (pkgSpecificReqs) {
                            return pkgSpecificReqs.includes(featureId);
                        }
                    }

                    // old feature.required were booleans, with true equivelant to 'before_submit'
                    return requiredVal === true || (Array.isArray(requiredVal) && requiredVal.includes('before_submit'));
                }
            } else if (beforeWhat === 'before_any_signature') {
                reqTest = function(requiredVal) {
                    return Array.isArray(requiredVal) && requiredVal.includes('before_any_signature');
                }
            } else { // signature ID
                reqTest = function(requiredVal, featureId = null) {
                    if (featureId === beforeWhat) return false;
                    return Array.isArray(requiredVal) && (requiredVal.includes('before_any_signature') || requiredVal.includes(beforeWhat));
                }
            }

            const tr = rootGetters['lang/tr'];
            const defaultMsg = tr.fieldIsRequired;
            function addField(id, message = undefined) {
                if (!message) message = defaultMsg
                incomplete.push({ id, message });
            }

            let incomplete = [];
            for (let form of rootGetters['form/forms']) {
                for (let feature of rootGetters['form/featuresByForm'](form.id)) {
                    if (!reqTest(feature.required, feature.id, feature.type)) continue; // field not required

                    let fieldData = getters.fieldData(form.id, feature.id);
                    if (feature.type === 'signature') {
                        if (!fieldData.signed) {
                            addField(feature.id, tr.signatureIsRequired);
                        }
                    } else if (feature.type === 'checkbox' && !fieldData.value) {
                        addField(feature.id, tr.mustBeChecked);
                    } else if (!fieldData.value) {
                        addField(feature.id, tr.fieldIsRequired);;
                    }
                }

                for (let groupId in form.data.featureGroups) {
                    let group = form.data.featureGroups[groupId];
                    if (!reqTest(group.required)) continue; // group not required

                    let groupData = getters.fieldData(form.id, groupId);
                    if (!groupData.value) {
                        const groupFeatures = rootGetters['form/featuresByGroupId'](groupId)
                        if (!groupFeatures.length) continue;
                        addField(
                            groupFeatures[0].id,
                            group.type === 'checkbox' ? tr.optionMustBeSelected : tr.fieldMustBeSelected
                        );
                    }
                }
            }

            return incomplete;
        },
    },
    mutations: {
        defaultFieldValues(state, { formId, values }) {
            for (let fieldId in values) {
                if (values[fieldId] === null) continue;
                state.formData[formId][fieldId].value = values[fieldId];
            }
        },
        initializeFormData(state, { formId, formData = null }) {
            if (formData) {
                // Overwrite form data with this saved data
                state.formData[formId] = { ...state.formData[formId], ...cloneDeep(formData) };
            } else {
                // Initialize using the existing default values
            }
            formData = state.formData[formId];

            for (let fieldId in formData) {
                let field = state.fields[fieldId];
                let data = formData[fieldId];
                if (!field) continue;

                _setMathScope(state.mathScope[formId], field, data);

                if (field.type === 'signature') {
                    state.signatureLocks[formId][fieldId] = !!data.signed;
                }
            }
        },
        fieldData(state, { formId, fieldId, data, updateDependents = true }) {
            let field = state.fields[fieldId];
            if (!field) return;

            if (data.value && field.convertTextTo === 'upper') data.value = data.value.toUpperCase();
            if (data.value && field.convertTextTo === 'lower') data.value = data.value.toLowerCase();

            if (!state.formData[formId]) state.formData[formId] = {};
            if (!state.formData[formId][fieldId]) state.formData[formId][fieldId] = {};
            state.formData[formId][fieldId] = { ...state.formData[formId][fieldId], ...data };

            _setMathScope(state.mathScope[formId], field, state.formData[formId][fieldId]);

            state.hasUnsavedChanges = true;

            if (updateDependents) {
                _updateDependents(state, formId, fieldId);
            }

            // If this is signature field, update field locks
            if (field.type === 'signature' && !data.signed) {
                state.signatureLocks[formId][fieldId] = false;
            }
        },
        updateDependents(state, { formId, fieldId }) {
            _updateDependents(state, formId, fieldId);
        },
        forms(state, forms) {
            // The root objects must be assigned to state after they are built for their props to be reactive
            let fields = {};
            let formData = {};
            let mathScope = {};
            let fieldNameMap = {};
            let dependents = {};
            const needsSelfCalc = new Set();
            let signatureLocks = {};
            for (let form of forms) {
                formData[form.id] = {};
                mathScope[form.id] = {};
                signatureLocks[form.id] = {};
                fieldNameMap[form.id] = {};
                dependents[form.id] = {};
                for (let page of form.data.pages) {
                    for (let feature of page.features) {
                        // Retain existing data if this form & field was already loaded
                        if (state.formData[form.id] && state.formData[form.id][feature.id]) {
                            formData[form.id][feature.id] = state.formData[form.id][feature.id];
                            mathScope[form.id][feature.name] = state.mathScope[form.id][feature.name] || '';
                            if (feature.type === 'signature') {
                                signatureLocks[form.id][feature.id] = state.signatureLocks[form.id][feature.id];
                            }
                        } else {
                            if (feature.defaultData) {
                                formData[form.id][feature.id] = cloneDeep(feature.defaultData);
                                mathScope[form.id][feature.name] = formData[form.id][feature.id];
                            } else {
                                formData[form.id][feature.id] = { value: '' };
                                mathScope[form.id][feature.name] = '';
                            }
                            if (feature.type === 'signature') {
                                signatureLocks[form.id][feature.id] = false;
                            }
                        }

                        fields[feature.id] = feature;
                        fieldNameMap[form.id][feature.name] = feature.id;
                        if (feature.calculate) {
                            let regex = /(?:^|[^\w.])(\w*[a-z]\w*)(?![\w(])/ig, match;
                            while (match = regex.exec(feature.calculate)) {
                                let fieldName = match[1];
                                if (!dependents[form.id][fieldName]) {
                                    dependents[form.id][fieldName] = new Set();
                                }
                                dependents[form.id][fieldName].add(feature.id);
                            }
                            needsSelfCalc.add(feature.id);
                        }
                    }
                }
                for (let groupId in form.data.featureGroups) {
                    let group = form.data.featureGroups[groupId];

                    // Retain existing data if this form & field was already loaded
                    if (state.formData[form.id] && state.formData[form.id][groupId]) {
                        formData[form.id][groupId] = state.formData[form.id][groupId];
                        mathScope[form.id][group.name] = state.mathScope[form.id][group.name] || '';
                    } else {
                        formData[form.id][groupId] = { value: '' };
                        mathScope[form.id][group.name] = '';
                    }

                    fields[groupId] = group;
                    fieldNameMap[form.id][group.name] = groupId;
                }
            }
            state.fields = fields;
            state.formData = formData;
            state.mathScope = mathScope;

            // Filter out invalid field name matches from calc dependents
            let filteredDeps = {};
            for (let formId in dependents) {
                for (let fieldName in dependents[formId]) {
                    let fieldId = fieldNameMap[formId][fieldName];
                    if (!fieldId) continue;
                    filteredDeps[fieldId] = [];
                    for (const depId of dependents[formId][fieldName]) {
                        filteredDeps[fieldId].push(depId);
                        // Fields which are dependent on another field don't require a self calculation
                        needsSelfCalc.delete(depId);
                    }
                }
            }

            // Mark fields which aren't dependent on any other field as needing one self calculation on load
            for (const selfCalcId of needsSelfCalc) {
                if (!filteredDeps[selfCalcId]) filteredDeps[selfCalcId] = [];
                // Must calculate self first, so deps can use the self calc result
                filteredDeps[selfCalcId].unshift(CALCULATE_SELF);
            }

            state.dependents = filteredDeps;
            state.signatureLocks = signatureLocks;
        },
        scrollPageIndex(state, scrollPageIndex) {
            state.scrollPageIndex = scrollPageIndex;
        },
        scrolledToItem(state, itemIndex) {
            state.scrolledToItem = itemIndex;
        },
        hasUnsavedChanges(state, hasUnsavedChanges) {
            state.hasUnsavedChanges = hasUnsavedChanges;
        },
        isOffice(state, isOffice) {
            state.isOffice = isOffice;
        },
        submittedByFname(state, fname) {
            state.submittedByFname = fname;
        },
        submittedByLname(state, lname) {
            state.submittedByLname = lname;
        },
        submittedByEmail(state, email) {
            state.submittedByEmail = email;
        },
        revisionRef(state, ref) {
            state.revisionRef = ref;
        },
        signingFeature(state, featureInfo) {
            state.signingFeature = featureInfo;
        },
        isLoaded(state, isLoaded) {
            state.isLoaded = isLoaded;
        },
        clearFormData(state) {
            state.fields = {};
            state.formData = {};
            state.mathScope = {};
            state.dependents = {};
            state.signatureLocks = {};
        },
        calculateAll(state, formIds = null) {
            if (formIds) {
                for (let formId of formIds) {
                    _updateDependents(state, formId);
                }
            } else {
                _updateDependents(state);
            }
        },
        recipient(state, recipient) {
            state.recipient = recipient || null;
        },
        highlightedSignatureId(state, id) {
            state.highlightedSignatureId = null;
            state.highlightedSignatureId = id;
        },
        pin(state, pin) {
            state.pin = pin || '';
        },
        invalidIds(state, ids) {
            state.invalidIds = ids;
        },
        removeInvalidId(state, id) {
            let index = state.invalidIds.indexOf(id);
            if (index !== -1) state.invalidIds.splice(index, 1);
        },
    },
    actions: {
        async load({ rootGetters, commit, dispatch }, {
            token = null,
            altRegionCode = null,
            linkType = '',
            id = null,
            revisionRef = null,
            pin = '',
            paymentId = ''
        }) {
            commit('isLoaded', false);
            if (token) {
                await dispatch('package/loadFromToken', { token, altRegionCode, linkType, pin, paymentId }, { root: true });
                commit('revisionRef', '');
                commit('pin', pin);
            } else if (id) {
                await dispatch('package/loadFromId', id, { root: true })
                commit('revisionRef', '');
            } else if (revisionRef) {
                await dispatch('package/loadFromRef', revisionRef, { root: true });
                commit('revisionRef', revisionRef);
            } else {
                // just load from current state
            }

            let pkg = rootGetters['package/pkg'];
            commit('forms', pkg.forms);
            commit('form/setForms', pkg.forms, { root: true });
            commit('hasUnsavedChanges', false);
            
            let formsWithoutData = [];
            for (let formId in pkg.form_instances) {
                dispatch('initializeDefaultValues', formId);

                let instance = pkg.form_instances[formId];
                let formData = null;
                if (isEmpty(instance.form_data)) {
                    formsWithoutData.push(formId);
                } else {
                    formData = instance.form_data;
                }
                commit('initializeFormData', { formId, formData });
            }

            commit('isLoaded', true);
            setTimeout(() => {
                commit('calculateAll', formsWithoutData);
            });
        },
        async save({ state, dispatch }, submit = false) {
            await dispatch('package/fillerSave', {
                submit,
                formData: state.formData,
                submittedByFname: submit ? state.submittedByFname : '',
                submittedByLname: submit ? state.submittedByLname : '',
                submittedByEmail: state.submittedByEmail,
            }, { root: true });
        },
        previewForms({ commit, dispatch }, forms) {
            commit('clearFormData');
            commit('forms', forms);

            // Build a display sequence using the forms & attachments
            commit('package/reset', null, { root: true });
            let sequence = [];
            let attachments = {};
            for (let form of forms) {
                sequence.push({ type: 'form', id: form.id });
                for (let attachment of form.data.attachments) {
                    attachments[attachment.id] = cloneDeep(attachment);
                    sequence.push({ type: 'attachment', id: attachment.id });
                }

                dispatch('initializeDefaultValues', form.id);
                commit('initializeFormData', { formId: form.id });
            }
            commit('package/attachments', attachments, { root: true });
            commit('package/sequence', sequence, { root: true });
            commit('isLoaded', true);
            setTimeout(() => {
                commit('calculateAll');
            });
        },
        initializeDefaultValues({ getters, commit, rootGetters }, formId) {
            // Load default initial values for all fields
            let formFeatures = rootGetters['form/featuresByForm'](formId);
            let defaultValues = {};
            for (let feature of formFeatures) {
                defaultValues[feature.id] = getters.fieldDefaultValue(feature.id);

                // Allow query string params to pre-populate form in office view
                if (getters.isOffice) {
                    let queryVal = getters.fieldQueryStringValue(feature.id);
                    if (queryVal !== null && queryVal !== defaultValues[feature.id]) {
                        defaultValues[feature.id] = queryVal;
                        commit('hasUnsavedChanges', true);
                    }
                }
            }

            // Load default initial values for feature groups
            let groups = rootGetters['form/featureGroupsByFormId'](formId);
            for (let groupId in groups) {
                let group = groups[groupId];
                let val = '';
                if (group.type === 'checkbox') {
                    let features = rootGetters['form/featuresByGroupId'](groupId);
                    for (let feature of features) {
                        if (defaultValues[feature.id]) {
                            val = defaultValues[feature.id];
                            break;
                        }
                    }
                }
                defaultValues[groupId] = val;
            }

            commit('defaultFieldValues', { formId, values: defaultValues });
        },
        async voidSignatures({ state, commit, dispatch, rootGetters }, { formId, featureId }) {
            let signatures = [];
            let signers = [];
            for (let fid in state.formData[formId]) {
                let data = state.formData[formId][fid];
                if (!data.signed) continue;
                signatures.push(fid);
                let name = `${data.fname} ${data.lname}`;
                if (signers.indexOf(name) < 0) {
                    signers.push(name);
                }
            }

            const tr = rootGetters['lang/tr'];

            let res = await dispatch('dialog/set', {
                type: 'confirm',
                header: tr.voidSignature(signatures.length),
                body: tr.voidSignatureDesc(signatures.length, signers),
                critical: true,
                okText: tr.voidSignatureEdit(signatures.length),
            }, { root: true });
            if (!res) return false;

            for (let fieldId of signatures) {
                commit('fieldData', {
                    formId,
                    fieldId,
                    data: cloneDeep(state.fields[fieldId].defaultData),
                });
            }
            return true;
        },
        updateAttachment({ state, commit }, { id, data }) {
            for (let key in data) {
                let value = data[key];
                if (key === 'files') {
                    if (state.isOffice) {
                        commit('package/updateAttachment', {
                            id,
                            key: 'is_filler_attachment',
                            value: value.length === 0
                        }, { root: true });
                    }
                } else if (!state.isOffice) {
                    continue;
                }
                commit('package/updateAttachment', { id, key, value }, { root: true });
                commit('hasUnsavedChanges', true);
            }
        },
        async agreeToTos({ getters, rootGetters }) {
            if (getters.recipient && !getters.recipient.agreeToTos) {
                let type = rootGetters['package/tokenType'];
                let altRegion = rootGetters['package/altRegionCode'];
                let token = rootGetters['package/accessToken'];
                await api.put(`filler/${type}/${altRegion}/${token}/agreeToTos`, { agree: true });
            }
        },
        async validateFieldValues({ getters, rootGetters, dispatch }) {
            let type = rootGetters['package/tokenType'];
            let altRegion = rootGetters['package/altRegionCode'];
            let token = rootGetters['package/accessToken'];
            let invalidIds = await api.post(`filler/validate/${type}/${altRegion}/${token}`, { formData: getters.formData });
            if (invalidIds.length) {
                let message = tr.fieldIncorrectFormatting(invalidIds.length);
                dispatch('markInvalidInputs', { ids: invalidIds, message });
                return false;
            }

            return true;
        },
        async markInvalidInputs({ commit, rootGetters }, { ids = [], message = undefined }) {
            if (!ids.length) return;
            const tr = rootGetters['lang/tr'];

            commit('tooltip/clear', null, { root: true });
            dom.scrollIntoView(`#${ids[0]}`, 'center');

            await timeout();
            commit('tooltip/set', {
                message: message || tr.invalidValue,
                trigger: `#${ids[0]}`,
                class: 'error',
            }, { root: true });

            await timeout();
            commit('invalidIds', ids);
        }
    }
};

function _updateDependents(state, formId = null, fieldId = null) {
    let fieldToForm = {}, modIds;
    if (fieldId) {
        // Updating dependents for one changed field
        modIds = [fieldId];
    } else if (formId) {
        // Updating dependants for all fields of one form
        modIds = Object.keys(state.formData[formId]);
    } else {
        // Updating dependents for all fields of all forms
        modIds = [];
        for (let frmId in state.formData) {
            for (let fldId in state.formData[frmId]) {
                modIds.push(fldId);
                fieldToForm[fldId] = frmId;
            }
        }
    }

    let loops = 0, modId;
    while (modId = modIds.shift()) {
        if (!state.dependents[modId]) continue;
        if (loops++ > 9999) {
            console.error('Field calculation circular dependency detected');
            break;
        }

        let frmId = formId || fieldToForm[modId];
        for (let i = 0; i < state.dependents[modId].length; i++) {
            let depId = state.dependents[modId][i];
            if (depId === CALCULATE_SELF) {
                depId = modId;
                state.dependents[modId].splice(i, 1);
                i--;
            } else if (!modIds.includes(depId)) {
                modIds.push(depId);
            }
            if (_isFeatureLocked(state, frmId, depId)) continue;

            let depField = state.fields[depId];
            let result;
            try {
                // Replace newlines or else weird stuff happens (like square brackets added around strings)
                const calc = depField.calculate.replace(/[\r\n\t ]+/g, " ")
                result = math.evaluate(calc, state.mathScope[frmId]);
                if (result === null || result === undefined || typeof result === 'function') {
                    result = '';
                } else if (typeof result === 'object' && result.value !== undefined) {
                    result = result.value ? result.value.toString() : '';
                } else {
                    result = result.toString();
                }
            } catch (err) {
                console.warn(err.message);
                // Can't show the error message in currency fields since it could contain numbers which would
                // immediately be masked into a fake number, breaking payment calculations on the backend if
                // submitted. Set to "CALC ERROR" instead to blank the field and guarantee payment calculations
                // will fail.
                result = depField.currency ? 'CALC ERROR' : err.message;
            }

            if (depField.convertTextTo === 'upper') result = result.toUpperCase();
            if (depField.convertTextTo === 'lower') result = result.toLowerCase();

            state.formData[frmId][depId].value = result;
            state.mathScope[frmId][depField.name] = result;
        }
    }
}

function _isFeatureLocked(state, formId, featureId) {
    let feature = state.fields[featureId] || {};
    if (!feature.lock || feature.lock[0] === 'never') return false;
    for (let sigId in state.signatureLocks[formId]) {
        if (
            state.signatureLocks[formId][sigId] && (
                feature.lock[0] === 'any' ||
                feature.lock.includes(sigId)
            )
        ) {
            return true;
        }
    }
    return false;
}

function _setMathScope(formScope, field, data) {
    if (typeof formScope[field.name] === 'object') {
        formScope[field.name] = data;
    } else if (data.value !== undefined) {
        let val = data.value;
        if (typeof val === 'string') {
            if (field.currency || /^\s*[$€£]?\s*\d+(\.\d+)?\s*[$€£]?\s*$/.test(val)) {
                // Remove currency symbols
                val = val.replace(/[^0-9\.]/g, '');
            } else if (/^\s*\d+(\.\d+)?%\s*$/.test(val)) {
                // Remove percent symbol and convert to decimal for muliplication
                val = val.replace(/[^0-9\.]/g, '') / 100;
            }
        }
        formScope[field.name] = val;
    }
}